export type CameraEffect = "none" | "blur" | "blurStrong";

export function isCameraEffect(value: string): value is CameraEffect {
  return value === "none" || value === "blur" || value === "blurStrong";
}

const BLUR_RADIUS: Record<Exclude<CameraEffect, "none">, number> = {
  blur: 10,
  blurStrong: 22,
};

const MASK_FEATHER = 3;

const SEGMENT_WIDTH = 256;

const SEGMENTATION_BASE = import.meta.env.BSMEET_SEGMENTATION_BASE;
const WASM_PATH = `${SEGMENTATION_BASE}/wasm`;
const MODEL_PATH = `${SEGMENTATION_BASE}/selfie_segmenter.tflite`;

export function effectsSupported(): boolean {
  if (typeof document === "undefined") return false;
  const canvas = document.createElement("canvas");
  return (
    typeof canvas.captureStream === "function" &&
    typeof HTMLVideoElement !== "undefined" &&
    "requestVideoFrameCallback" in HTMLVideoElement.prototype
  );
}

type Segmenter = {
  segmentForVideo: (
    frame: HTMLCanvasElement,
    timestamp: number,
  ) => { confidenceMasks?: Mask[] | undefined; close: () => void };
  getLabels: () => string[];
  close: () => void;
};

type Mask = {
  width: number;
  height: number;
  getAsFloat32Array: () => Float32Array;
  close: () => void;
};

function personIndex(labels: string[]): number {
  const found = labels.findIndex((label) => /person|selfie|foreground/iu.test(label));
  return found === -1 ? Math.max(0, labels.length - 1) : found;
}

export class CameraEffects {
  #video: HTMLVideoElement | null = null;
  #canvas: HTMLCanvasElement | null = null;
  #context: CanvasRenderingContext2D | null = null;
  #person: HTMLCanvasElement | null = null;
  #small: HTMLCanvasElement | null = null;
  #mask: HTMLCanvasElement | null = null;
  #maskImage: ImageData | null = null;
  #segmenter: Segmenter | null = null;
  #personAt = 0;
  #output: MediaStreamTrack | null = null;
  #source: MediaStreamTrack | null = null;
  #effect: Exclude<CameraEffect, "none"> = "blur";
  #frame: number | null = null;
  #running = false;

  get output(): MediaStreamTrack | null {
    return this.#output;
  }

  async start(
    source: MediaStreamTrack,
    effect: Exclude<CameraEffect, "none">,
  ): Promise<MediaStreamTrack> {
    this.#effect = effect;
    if (this.#running && this.#source === source && this.#output !== null) return this.#output;

    this.stop();
    this.#source = source;

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([source]);
    await video.play();
    this.#video = video;

    const settings = source.getSettings();
    const width = settings.width ?? video.videoWidth ?? 1280;
    const height = settings.height ?? video.videoHeight ?? 720;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { alpha: false });
    if (context === null) throw new Error("This browser cannot composite a camera effect");
    this.#canvas = canvas;
    this.#context = context;

    const person = document.createElement("canvas");
    person.width = width;
    person.height = height;
    this.#person = person;

    const small = document.createElement("canvas");
    small.width = SEGMENT_WIDTH;
    small.height = Math.max(1, Math.round((SEGMENT_WIDTH * height) / width));
    this.#small = small;

    this.#segmenter = await createSegmenter();
    this.#personAt = personIndex(this.#segmenter.getLabels());

    const stream = canvas.captureStream(settings.frameRate ?? 30);
    const [output] = stream.getVideoTracks();
    if (output === undefined) throw new Error("This browser cannot capture a canvas");
    this.#output = output;

    this.#running = true;
    this.#schedule();
    return output;
  }

  update(effect: Exclude<CameraEffect, "none">): void {
    this.#effect = effect;
  }

  stop(): void {
    this.#running = false;
    const video = this.#video;
    if (video !== null && this.#frame !== null) video.cancelVideoFrameCallback(this.#frame);
    this.#frame = null;

    if (video !== null) {
      video.pause();
      video.srcObject = null;
    }

    this.#segmenter = null;
    this.#video = null;
    this.#canvas = null;
    this.#context = null;
    this.#person = null;
    this.#small = null;
    this.#mask = null;
    this.#maskImage = null;
    this.#output = null;
    this.#source = null;
  }

  #schedule(): void {
    const video = this.#video;
    if (!this.#running || video === null) return;
    this.#frame = video.requestVideoFrameCallback((now) => {
      try {
        this.#draw(now);
      } catch (error) {
        console.error("camera effect frame draw failed:", error);
      }
      this.#schedule();
    });
  }

  #draw(now: number): void {
    const video = this.#video;
    const canvas = this.#canvas;
    const context = this.#context;
    const person = this.#person;
    const small = this.#small;
    const segmenter = this.#segmenter;
    if (video === null || canvas === null || context === null || person === null) return;
    if (video.videoWidth === 0) return;
    if (segmenter === null || small === null) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      return;
    }

    const smallContext = small.getContext("2d", { willReadFrequently: false });
    if (smallContext === null) return;
    smallContext.drawImage(video, 0, 0, small.width, small.height);

    const stamp = nextStamp(now);

    let result;
    try {
      result = segmenter.segmentForVideo(small, stamp);
    } catch {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      return;
    }
    const mask = result.confidenceMasks?.[this.#personAt];
    if (mask === undefined) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      result.close();
      return;
    }

    const cutout = this.#maskCanvas(mask);
    result.close();

    const personContext = person.getContext("2d");
    if (personContext === null) return;
    personContext.globalCompositeOperation = "copy";
    personContext.filter = "none";
    personContext.drawImage(video, 0, 0, person.width, person.height);
    personContext.globalCompositeOperation = "destination-in";

    personContext.filter = `blur(${MASK_FEATHER}px)`;
    personContext.drawImage(cutout, 0, 0, person.width, person.height);
    personContext.filter = "none";
    personContext.globalCompositeOperation = "source-over";

    const radius = (BLUR_RADIUS[this.#effect] * canvas.height) / 720;
    context.filter = `blur(${radius}px)`;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    context.filter = "none";
    context.drawImage(person, 0, 0, canvas.width, canvas.height);
  }

  #maskCanvas(mask: Mask): HTMLCanvasElement {
    let canvas = this.#mask;
    let image = this.#maskImage;
    if (canvas === null || canvas.width !== mask.width || canvas.height !== mask.height) {
      canvas = document.createElement("canvas");
      canvas.width = mask.width;
      canvas.height = mask.height;
      image = new ImageData(mask.width, mask.height);
      this.#mask = canvas;
      this.#maskImage = image;
    }
    if (image === null) return canvas;

    const values = mask.getAsFloat32Array();
    const pixels = image.data;
    for (let index = 0; index < values.length; index += 1) {
      pixels[index * 4 + 3] = (values[index] ?? 0) * 255;
    }
    mask.close();
    canvas.getContext("2d")?.putImageData(image, 0, 0);
    return canvas;
  }
}

let shared: Promise<Segmenter> | null = null;

let lastStamp = 0;

function nextStamp(now: number): number {
  lastStamp = Math.max(Math.round(now), lastStamp + 1);
  return lastStamp;
}

async function createSegmenter(): Promise<Segmenter> {
  shared ??= (async () => {
    const vision = await import("@mediapipe/tasks-vision");
    const fileset = await vision.FilesetResolver.forVisionTasks(WASM_PATH);
    return vision.ImageSegmenter.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_PATH, delegate: "GPU" },
      runningMode: "VIDEO",
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    });
  })();
  try {
    return await shared;
  } catch (error) {
    shared = null;
    throw error;
  }
}
